import React, { useState } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, Check, Scale, AlertTriangle } from 'lucide-react';

const STEPS = [
  { id: 1, label: 'Basic Setup' },
  { id: 2, label: 'Classification' },
  { id: 3, label: 'Forum Route' },
  { id: 4, label: 'Link Records' },
  { id: 5, label: 'Team Setup' },
  { id: 6, label: 'Procedural Setup' },
];

export default function CreateDisputeWizard({ projectId, settings, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const profile = settings?.profile || {};
  const family = settings?.contract_family || 'NEC';

  const [form, setForm] = useState({
    title: '', counterparty: '', originating_module: '',
    originating_record_id: '', date_disagreement_started: '', summary: '',
    clause_references: '', amount_in_dispute: '', time_in_dispute_days: '',
    risk_rating: 'medium', commercial_sensitivity: 'normal', currency: 'ZAR',
    classification: 'potential_dispute', forum_route: '',
    internal_owner_user_id: '', external_lawyer: '', delay_expert: '',
    quantum_expert: '', privileged_flag: false, generate_defaults: true,
  });

  const u = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Matter title is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        project_id: projectId,
        contract_family: family,
        amount_in_dispute: form.amount_in_dispute ? parseFloat(form.amount_in_dispute) : null,
        time_in_dispute_days: form.time_in_dispute_days ? parseInt(form.time_in_dispute_days) : null,
      };
      const res = await api.disputes.createMatter(projectId, payload);
      toast.success(`Matter ${res.data.matter_ref} created`);
      onCreated(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create matter');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="create-dispute-wizard">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Create Dispute Matter</h2>
          <p className="text-xs text-slate-500 mt-0.5">Step {step} of 6 — {STEPS[step - 1].label}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
      </div>

      {/* Warning Banner */}
      <div className="flex items-start gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">Platform defaults are starting assumptions only. Project-specific contract documents, particular conditions, and amendments override all defaults.</p>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center gap-1 mb-6">
        {STEPS.map(s => (
          <div key={s.id} className={`flex-1 h-1.5 rounded-full transition-colors ${step >= s.id ? 'bg-slate-900' : 'bg-slate-200'}`} />
        ))}
      </div>

      <Card>
        <CardContent className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label className="text-xs">Matter Title *</Label><Input value={form.title} onChange={e => u('title', e.target.value)} placeholder="e.g. Payment Certificate No. 12 Dispute" data-testid="wizard-title" /></div>
                <div><Label className="text-xs">Counterparty</Label><Input value={form.counterparty} onChange={e => u('counterparty', e.target.value)} placeholder="e.g. Employer / Client name" /></div>
                <div><Label className="text-xs">Originating Module</Label>
                  <Select value={form.originating_module} onValueChange={v => u('originating_module', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claims">Claims</SelectItem>
                      <SelectItem value="delay_events">Delay Events</SelectItem>
                      <SelectItem value="variations">Variations / CEs</SelectItem>
                      <SelectItem value="certificates">Certificates</SelectItem>
                      <SelectItem value="payments">Payments</SelectItem>
                      <SelectItem value="defects">Defects / Handover</SelectItem>
                      <SelectItem value="final_account">Final Account</SelectItem>
                      <SelectItem value="termination">Termination</SelectItem>
                      <SelectItem value="direct_manual">Direct / Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Date Disagreement Started</Label><Input type="date" value={form.date_disagreement_started} onChange={e => u('date_disagreement_started', e.target.value)} /></div>
                <div><Label className="text-xs">Contract Family</Label><Input value={family} disabled className="bg-slate-50" /></div>
              </div>
              <div><Label className="text-xs">Summary of Disagreement</Label><Textarea value={form.summary} onChange={e => u('summary', e.target.value)} placeholder="Describe the core disagreement..." rows={3} data-testid="wizard-summary" /></div>
              <div><Label className="text-xs">Clause References</Label><Input value={form.clause_references} onChange={e => u('clause_references', e.target.value)} placeholder="e.g. Clause 60.1(6), 61.1" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="text-xs">Amount in Dispute ({form.currency})</Label><Input type="number" value={form.amount_in_dispute} onChange={e => u('amount_in_dispute', e.target.value)} placeholder="0.00" /></div>
                <div><Label className="text-xs">Time in Dispute (days)</Label><Input type="number" value={form.time_in_dispute_days} onChange={e => u('time_in_dispute_days', e.target.value)} placeholder="0" /></div>
                <div><Label className="text-xs">Risk Rating</Label>
                  <Select value={form.risk_rating} onValueChange={v => u('risk_rating', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-700">Matter Classification</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'potential_dispute', label: 'Potential Dispute', desc: 'Early-stage disagreement, not yet formally crystallised' },
                  { val: 'formal_dispute', label: 'Formal Dispute', desc: 'Dispute has crystallised and requires contractual action' },
                  { val: 'negotiation_active', label: 'Settlement-Only Matter', desc: 'Actively pursuing negotiated resolution' },
                  { val: 'referral_in_preparation', label: 'Urgent Referral', desc: 'Immediate referral to forum required' },
                ].map(opt => (
                  <div key={opt.val} onClick={() => u('classification', opt.val)} className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${form.classification === opt.val ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`class-${opt.val}`}>
                    <h4 className="text-sm font-bold text-slate-800">{opt.label}</h4>
                    <p className="text-xs text-slate-500 mt-1">{opt.desc}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div><Label className="text-xs">Commercial Sensitivity</Label>
                  <Select value={form.commercial_sensitivity} onValueChange={v => u('commercial_sensitivity', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="sensitive">Sensitive</SelectItem><SelectItem value="highly_sensitive">Highly Sensitive</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={form.privileged_flag} onChange={e => u('privileged_flag', e.target.checked)} className="rounded" /> Privileged Matter</label>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-700">Forum Route — {family}</h3>
              <p className="text-xs text-slate-500">Select the procedural route applicable under your contract. Default steps will be generated automatically.</p>
              <div className="space-y-3">
                {(profile.forum_routes || []).map(route => (
                  <div key={route} onClick={() => u('forum_route', route)} className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${form.forum_route === route ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`route-${route}`}>
                    <h4 className="text-sm font-bold text-slate-800 capitalize">{route.replace(/_/g, ' ')}</h4>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg border">
                <h4 className="text-xs font-bold text-slate-600 mb-2">Contract Procedural Steps ({family})</h4>
                <div className="space-y-1">
                  {(profile.stages || []).map((s, i) => (
                    <div key={s.code} className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold">{i + 1}</span>
                      <span>{s.label}</span>
                      {s.optional && <Badge variant="outline" className="text-[8px]">Optional</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-700">Link Project Records</h3>
              <p className="text-xs text-slate-500">You can link claims, delay events, variations, correspondence, and other records to this dispute after creation via the Workspace.</p>
              <div className="p-8 text-center bg-slate-50 rounded-lg border border-dashed border-slate-300">
                <p className="text-sm text-slate-500">Record linking will be available in the matter workspace after creation.</p>
                <p className="text-xs text-slate-400 mt-1">You can link Claims, Delay Events, Variations, Certificates, RFIs, Site Instructions, Minutes, Photos, Defects, and more.</p>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-700">Internal Team Setup</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">Internal Owner / Lead</Label><Input value={form.internal_owner_user_id} onChange={e => u('internal_owner_user_id', e.target.value)} placeholder="Owner email or name" /></div>
                <div><Label className="text-xs">External Counsel</Label><Input value={form.external_lawyer} onChange={e => u('external_lawyer', e.target.value)} placeholder="External lawyer / firm" /></div>
                <div><Label className="text-xs">Delay Expert</Label><Input value={form.delay_expert} onChange={e => u('delay_expert', e.target.value)} placeholder="Delay analyst / expert" /></div>
                <div><Label className="text-xs">Quantum Expert</Label><Input value={form.quantum_expert} onChange={e => u('quantum_expert', e.target.value)} placeholder="Quantum / cost expert" /></div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-700">Initial Procedural Setup</h3>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.generate_defaults} onChange={e => u('generate_defaults', e.target.checked)} className="rounded" /> Auto-generate default procedural steps and deadline events based on {family} profile</label>
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h4 className="text-xs font-bold text-slate-600 mb-2">The system will auto-generate:</h4>
                <ul className="space-y-1 text-xs text-slate-600">
                  <li>Procedural timetable with contract-specific steps</li>
                  <li>Deadline events for each step with due dates</li>
                  <li>Calendar-based deadline tracking ({profile?.deadline_defaults?.calendar_type?.replace(/_/g, ' ') || 'calendar days'})</li>
                </ul>
              </div>
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-sm font-bold text-green-800">Ready to create</h4>
                <p className="text-xs text-green-700 mt-1">Title: <strong>{form.title || '(not set)'}</strong></p>
                <p className="text-xs text-green-700">Classification: <strong>{form.classification?.replace(/_/g, ' ')}</strong></p>
                <p className="text-xs text-green-700">Risk: <strong>{form.risk_rating}</strong></p>
                {form.forum_route && <p className="text-xs text-green-700">Forum: <strong>{form.forum_route.replace(/_/g, ' ')}</strong></p>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-4">
        <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> {step > 1 ? 'Back' : 'Cancel'}
        </Button>
        {step < 6 ? (
          <Button onClick={() => setStep(step + 1)} className="bg-slate-900 hover:bg-slate-800 gap-1" data-testid="wizard-next">
            Next <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 gap-1" data-testid="wizard-submit">
            <Check className="h-4 w-4" /> {submitting ? 'Creating...' : 'Create Matter'}
          </Button>
        )}
      </div>
    </div>
  );
}
